import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import Pagination from '../components/Pagination';
import { ThumbsUp, ThumbsDown, ExternalLink, Filter } from 'lucide-react';

const TemplateSuggestions: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterStatus, setFilterStatus] = useState('All');

    // Sample data
    const suggestions = [
        {
            id: 1,
            name: 'E-commerce Dashboard',
            category: 'Admin',
            description: 'A modern e-commerce dashboard with analytics, order management, and customer insights.',
            referenceUrl: 'https://example.com/template1',
            status: 'Approved',
            likes: 24,
            dislikes: 3,
            submittedBy: 'John Doe',
            submittedDate: '2025-02-15',
        },
        {
            id: 2,
            name: 'Portfolio for Photographers',
            category: 'Portfolio',
            description: 'A minimalist portfolio template designed specifically for photographers with gallery features.',
            referenceUrl: 'https://example.com/template2',
            status: 'Pending',
            likes: 18,
            dislikes: 2,
            submittedBy: 'Sarah Johnson',
            submittedDate: '2025-02-20',
        },
        {
            id: 3,
            name: 'Blog with Newsletter',
            category: 'Blog',
            description: 'A blog template with integrated newsletter subscription and social sharing features.',
            referenceUrl: 'https://example.com/template3',
            status: 'Reviewed',
            likes: 32,
            dislikes: 5,
            submittedBy: 'Michael Brown',
            submittedDate: '2025-02-22',
        },
        {
            id: 4,
            name: 'Real Estate Listing',
            category: 'Business',
            description: 'A template for real estate agencies with property listings, search, and filtering.',
            referenceUrl: 'https://example.com/template4',
            status: 'Rejected',
            likes: 7,
            dislikes: 15,
            submittedBy: 'Emily Davis',
            submittedDate: '2025-02-25',
        },
        {
            id: 5,
            name: 'Restaurant Menu',
            category: 'Business',
            description: 'A responsive restaurant template with online menu, reservation system, and location map.',
            referenceUrl: 'https://example.com/template5',
            status: 'Approved',
            likes: 41,
            dislikes: 2,
            submittedBy: 'David Wilson',
            submittedDate: '2025-02-28',
        },
    ];

    const statusColors = {
        Pending: 'warning',
        Reviewed: 'info',
        Approved: 'success',
        Rejected: 'danger',
    } as const;

    // Filter and search
    const filteredSuggestions = suggestions.filter((suggestion) => {
        const matchesSearch = suggestion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            suggestion.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            suggestion.category.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'All' || suggestion.status === filterStatus;

        return matchesSearch && matchesFilter;
    });

    // Pagination
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredSuggestions.length / itemsPerPage);
    const paginatedSuggestions = filteredSuggestions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Template Suggestions</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Review and manage template suggestions from users
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <div className="flex items-center space-x-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="All">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="Reviewed">Reviewed</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                    </select>

                    <Button variant="primary">
                        New Suggestion
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                {paginatedSuggestions.length > 0 ? (
                    paginatedSuggestions.map((suggestion) => (
                        <Card key={suggestion.id} className="hover:shadow-md transition-shadow">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center">
                                        <h3 className="text-lg font-medium text-gray-900">{suggestion.name}</h3>
                                        <Badge
                                            variant={statusColors[suggestion.status as keyof typeof statusColors]}
                                            className="ml-2"
                                        >
                                            {suggestion.status}
                                        </Badge>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">Category: {suggestion.category}</p>
                                    <p className="mt-2 text-sm text-gray-700">{suggestion.description}</p>
                                    <div className="mt-2 flex items-center text-sm text-gray-500">
                                        <a
                                            href={suggestion.referenceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                        >
                                            Reference <ExternalLink size={14} className="ml-1" />
                                        </a>
                                        <span className="mx-2">•</span>
                                        <span>Submitted by {suggestion.submittedBy}</span>
                                        <span className="mx-2">•</span>
                                        <span>{suggestion.submittedDate}</span>
                                    </div>
                                </div>

                                <div className="flex flex-col items-end space-y-4">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex items-center text-green-600">
                                            <ThumbsUp size={18} />
                                            <span className="ml-1">{suggestion.likes}</span>
                                        </div>
                                        <div className="flex items-center text-red-600">
                                            <ThumbsDown size={18} />
                                            <span className="ml-1">{suggestion.dislikes}</span>
                                        </div>
                                    </div>

                                    <div className="flex space-x-2">
                                        {suggestion.status === 'Pending' && (
                                            <>
                                                <Button variant="info" size="sm">
                                                    Review
                                                </Button>
                                                <Button variant="success" size="sm">
                                                    Approve
                                                </Button>
                                                <Button variant="danger" size="sm">
                                                    Reject
                                                </Button>
                                            </>
                                        )}
                                        {suggestion.status === 'Reviewed' && (
                                            <>
                                                <Button variant="success" size="sm">
                                                    Approve
                                                </Button>
                                                <Button variant="danger" size="sm">
                                                    Reject
                                                </Button>
                                            </>
                                        )}
                                        {(suggestion.status === 'Approved' || suggestion.status === 'Rejected') && (
                                            <Button variant="secondary" size="sm">
                                                Change Status
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))
                ) : (
                    <Card>
                        <div className="text-center py-6">
                            <p className="text-gray-500">No template suggestions found.</p>
                        </div>
                    </Card>
                )}
            </div>

            {filteredSuggestions.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default TemplateSuggestions;